import { Chess } from 'chess.js';
import type { MistakePayload } from './api';

/**
 * Profondeur Stockfish utilisée pour l'autopsie. depth 16 est un compromis :
 * ~1.5s par eval sur Stockfish WASM multi-thread → ~90s pour une partie de 60 plies
 * en n'analysant que les coups du joueur (cf. WAKEUP_MVP1.1 T2).
 */
export const AUTOPSY_DEPTH = 16;

/**
 * En dessous, on ne stocke même pas la mistake. Le seuil de 50cp filtre le bruit
 * Stockfish à depth 16 (±30-50cp de variance entre 2 evals consécutives sur le
 * bestmove). Le backend classe en `inaccuracy` à partir de 30cp mais ne crée pas
 * de carte avant 80cp — uploader sous 50cp = upload de bruit pur.
 *
 * Spec : docs/WAKEUP_MVP1.2.md §3 T0 important #1.
 */
export const STORE_THRESHOLD_CP = 50;

/** Plafond plié pour éviter les overflow SMALLINT côté DB (eval_drop_cp). */
const MAX_CP = 32000;
const MATE_PROXY_CP = 10000;

export interface EngineLike {
  analyze(
    fen: string,
    depth: number,
  ): Promise<{
    scoreCp: number | null;
    scoreMate: number | null;
    bestmove: string | null;
  }>;
}

export interface AutopsyProgress {
  current: number;
  total: number;
  mistakesSoFar: number;
}

export type ProgressCallback = (p: AutopsyProgress) => void;

export interface LookupLike {
  /**
   * Retourne le coup attendu si la position est connue dans le répertoire,
   * `null` sinon (404 backend). Doit avaler les erreurs réseau en `null`
   * pour ne pas faire planter l'autopsie complète.
   */
  lookupFen(fen: string): Promise<{ expected_move: string } | null>;
}

/** Au-delà du ply 20 on considère qu'on est sortis du livre (phase opening). */
const OPENING_PHASE_MAX_PLY = 20;

export interface AutopsyResult {
  mistakes: MistakePayload[];
  /** 1er ply (Léon) en phase opening dont la FEN n'est pas dans le répertoire. */
  outOfBookPly: number | null;
}

/**
 * Pour chaque coup joué par Léon dans la partie :
 *   1. eval Stockfish à profondeur AUTOPSY_DEPTH sur la position AVANT le coup
 *      (score from-side-to-move) ;
 *   2. joue le coup, eval sur la position APRÈS (score from opponent's side) ;
 *   3. compare en POV Léon : drop = scoreBefore - (-scoreAfter) = scoreBefore + scoreAfter.
 *
 * Les évaluations en mate sont projetées sur ±MATE_PROXY_CP pour rester en cp comparable.
 *
 * Si `lookup` est fourni, on détecte aussi le 1er ply (en phase opening) qui sort
 * du répertoire (404 sur `/api/v1/opening/lookup`). Les mistakes survenant à ou
 * après ce ply reçoivent `pattern_tags='out_of_book'` (T6).
 */
export async function runAutopsy(
  pgn: string,
  leonColor: 'white' | 'black',
  engine: EngineLike,
  onProgress?: ProgressCallback,
  depth: number = AUTOPSY_DEPTH,
  lookup?: LookupLike,
): Promise<AutopsyResult> {
  const reader = new Chess();
  try {
    reader.loadPgn(pgn);
  } catch {
    return { mistakes: [], outOfBookPly: null };
  }
  const history = reader.history({ verbose: true });
  if (history.length === 0) return { mistakes: [], outOfBookPly: null };

  const replay = new Chess();
  const mistakes: MistakePayload[] = [];
  const total = history.length;
  let outOfBookPly: number | null = null;

  for (let i = 0; i < total; i++) {
    const move = history[i]!;
    const movingColor = move.color === 'w' ? 'white' : 'black';
    const ply = i + 1;

    onProgress?.({ current: i, total, mistakesSoFar: mistakes.length });

    if (movingColor !== leonColor) {
      replay.move(move.san);
      continue;
    }

    const fenBefore = replay.fen();

    if (lookup && outOfBookPly === null && ply <= OPENING_PHASE_MAX_PLY) {
      try {
        const known = await lookup.lookupFen(fenBefore);
        if (known === null) {
          outOfBookPly = ply;
        }
      } catch {
        // network/auth error : silent — la détection out-of-book est best-effort
      }
    }

    const evalBefore = await engine.analyze(fenBefore, depth);
    const scoreBefore = projectScoreCp(evalBefore.scoreCp, evalBefore.scoreMate);

    replay.move(move.san);
    const fenAfter = replay.fen();
    const evalAfter = await engine.analyze(fenAfter, depth);
    const scoreAfterFromLeon = -projectScoreCp(evalAfter.scoreCp, evalAfter.scoreMate);

    const drop = Math.max(0, scoreBefore - scoreAfterFromLeon);
    if (drop >= STORE_THRESHOLD_CP) {
      mistakes.push({
        ply,
        fen_before: fenBefore,
        move_played: move.san,
        move_expected: evalBefore.bestmove ?? move.san,
        eval_drop_cp: Math.min(MAX_CP, drop),
      });
    }
  }

  if (outOfBookPly !== null) {
    for (const m of mistakes) {
      if (m.ply >= outOfBookPly) {
        m.pattern_tags = m.pattern_tags ? `${m.pattern_tags},out_of_book` : 'out_of_book';
      }
    }
  }

  onProgress?.({ current: total, total, mistakesSoFar: mistakes.length });
  return { mistakes, outOfBookPly };
}

/**
 * Convertit un score Stockfish en cp (centipawns) signés, en projetant les évals
 * `mate` sur ±MATE_PROXY_CP pour rester comparable.
 */
export function projectScoreCp(scoreCp: number | null, scoreMate: number | null): number {
  if (scoreMate !== null) {
    return scoreMate > 0 ? MATE_PROXY_CP : -MATE_PROXY_CP;
  }
  return scoreCp ?? 0;
}
